import { z } from 'zod';
import { getPusherFromWindow } from 'utils/window';
import { AccountIdSchema } from 'types/accounts';
import { GameIdSchema } from 'types/games';
var pusherSingleton;
var GameUpdateDataSchema = z.object({
    game_id: GameIdSchema
});
var AccountUpdateDataSchema = z.object({
    account_id: AccountIdSchema
});
var ActivateAppDataSchema = z.unknown();
var getPusher = function () {
    var pusherFromWindow = getPusherFromWindow();
    if (!pusherFromWindow) {
        throw new Error('Pusher is not defined');
    }
    return pusherFromWindow;
};
/**
 * Initialize the Pusher instance
 * @param pusherKey - The Pusher key
 */
export var initPusher = function (pusherKey) {
    if (pusherSingleton) {
        return;
    }
    var options = {
        wsHost: 'ws.pusherapp.com',
        httpHost: 'sockjs.pusher.com',
        encrypted: true
    };
    var PusherClass = getPusher();
    pusherSingleton = new PusherClass(pusherKey, options);
};
export var getAccountChannelName = function (accountId) { return "account-".concat(accountId); };
export var getGameChannelName = function (gameId) { return "game-".concat(gameId); };
export var getActivateAppChannelName = function (activationCode) { return "activate-".concat(activationCode); };
export var unsubscribeFromChannel = function (getChannelName) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getChannelName();
    pusherSingleton.unsubscribe(channelName);
};
/**
 * Subscribe to the account channel
 * @param accountId - The account ID
 * @param callback - The callback function
 */
export var subscribeToAccountChannel = function (accountId, callback) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getAccountChannelName(accountId);
    var channel = pusherSingleton.subscribe(channelName);
    // Unbind any existing 'update' listeners to prevent duplicate event handlers
    // This makes the function idempotent and safe to call multiple times (e.g., on HMR reload)
    channel.unbind('update');
    /**
     * Because the Pusher is not properly typed, I'm verifying the data before calling the callback
     * I think there should be a way in TS to better type the "subscribe" method so that the "data"
     * is typed When passed to the callback.
     */
    channel.bind('update', function (rawData) {
        var result = AccountUpdateDataSchema.safeParse(rawData);
        if (result.success) {
            callback(result.data);
        }
        else {
            console.log('rawData', rawData);
            console.error('Invalid account update data:', result.error);
        }
    });
};
/**
 * Subscribe to the game channel
 * @param gameId - The game ID
 * @param callback - The callback function
 */
export var subscribeToGameChannel = function (gameId, callback) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getGameChannelName(gameId);
    var channel = pusherSingleton.subscribe(channelName);
    // Unbind any existing 'update' listeners to prevent duplicate event handlers
    // This makes the function idempotent and safe to call multiple times (e.g., on HMR reload)
    channel.unbind('update');
    /**
     * Because the Pusher is not properly typed, I'm verifying the data before calling the callback
     * I think there should be a way in TS to better type the "subscribe" method so that the "data"
     * is typed When passed to the callback.
     */
    channel.bind('update', function (rawData) {
        var result = GameUpdateDataSchema.safeParse(rawData);
        if (result.success) {
            callback(result.data);
        }
        else {
            console.log('rawData', rawData);
            console.error('Invalid game update data:', result.error);
        }
    });
};
/**
 * Subscribe to the activate app channel
 * @param activationCode - The activation code
 * @param callback - The callback function
 */
export var subscribeToActivateAppChannel = function (activationCode, callback) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getActivateAppChannelName(activationCode);
    var channel = pusherSingleton.subscribe(channelName);
    channel.bind('activate', callback);
};
